import { z } from "zod";

// --- GLOBAL METADATA SCHEMA ---
export const ProductsMetaSchema = z.object({
  total: z.number().default(0),
  page: z.number().default(1),
  limit: z.number().default(12),
  totalPages: z.number().default(1),
  hasNextPage: z.boolean().nullish().default(false),
  hasPrevPage: z.boolean().nullish().default(false),
}).passthrough();
export const ReviewSchema = z.object({
  id: z.string(),
  productId: z.string(),
  userId: z.string().nullish(),
  rating: z.number(),
  comment: z.string(),
  name: z.string().nullish(),
  email: z.string().email().nullish(),
  isApproved: z.boolean().nullish().transform(val => val ?? false),
  createdAt: z.string(),
  updatedAt: z.string(),
  product: z.object({
    name: z.string(),
    slug: z.string(),
    images: z.array(z.object({
      url: z.string(),
      isPrimary: z.boolean().optional(),
    })),
  }).nullish(),
  user: z.object({
    name: z.string(),
    email: z.string().email().nullish(),
  }).nullish(),
}).passthrough();

export const ReviewStatsSchema = z.object({
  totalReviews: z.number().default(0),
  approvedReviews: z.number().default(0),
  rejectedReviews: z.number().default(0),
  averageRating: z.number().default(0),
  reviewsPerDay: z.array(z.object({
    date: z.string(),
    count: z.number(),
  })).default([]),
}).passthrough();

export const PaginatedReviewResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().nullish(),
  data: z.object({
    data: z.array(ReviewSchema),
    meta: ProductsMetaSchema,
    stats: ReviewStatsSchema.nullish(),
  }).passthrough(),
}).passthrough();

export const CreateReviewResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().nullish(),
  data: ReviewSchema,
}).passthrough();
export const ProductImageSchema = z.object({
  url: z.string(),
  isPrimary: z.boolean().optional(),
});

export const AttributeOptionSchema = z.object({
  id: z.string(),
  attributeId: z.string(),
  label: z.string(),
  value: z.union([z.string(), z.number()]).nullish(),
  price: z.union([z.string(), z.number()]).nullish().default(null),
  isEnabled: z.boolean().nullish().default(true),
  position: z.number().default(0),
  createdAt: z.string().nullish(),
}).passthrough();

export const AttributeSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  unit: z.string().nullish().default(""),
  isGlobal: z.boolean().nullish().default(false),
  createdAt: z.string().nullish().default(""),
  options: z.array(AttributeOptionSchema).nullish().default([]),
}).passthrough();

export const ProductAttributeSchema = z.object({
  id: z.string(),
  productId: z.string(),
  attributeId: z.string(),
  isRequired: z.boolean().nullish().default(false),
  attribute: AttributeSchema,
}).passthrough();

export const CategorySchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  level: z.number().optional(),
  parentId: z.string().nullable().optional(),
});

export const ProductSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullish().default(""),
  status: z.enum(["ACTIVE", "INACTIVE", "DRAFT"]).default("ACTIVE"),
  sku: z.string().nullish().default(""),
  price: z.union([z.string(), z.number()]).default(0),
  originalPrice: z.union([z.string(), z.number()]).nullish(),
  salePrice: z.union([z.string(), z.number()]).nullish(),
  stock: z.number().default(0),
  size: z.string().nullish().default(""),
  createdAt: z.string().nullish(),
  updatedAt: z.string().nullish(),
  image: z.string().url().nullish(),
  categories: z.array(z.object({
    categoryId: z.string(),
    category: CategorySchema.nullish(),
  })).nullish().default([]),
  attributes: z.array(ProductAttributeSchema).nullish().default([]),
  images: z.array(ProductImageSchema).nullish().default([]),
  reviews: z.array(ReviewSchema).nullish().default([]),
}).passthrough();

export const MinimalProductSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string().nullish().default(""),
  sku: z.string().nullish().default(""),
  images: z.array(ProductImageSchema).nullish().default([]),
}).passthrough();

// --- API RESPONSE SCHEMAS ---
export const PaginatedProductResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().nullish(),
  data: z.object({
    data: z.array(ProductSchema),
    meta: ProductsMetaSchema,
  }).passthrough(),
}).passthrough();

export const ProductResponseSchema = z.object({
  success: z.boolean(),
  data: ProductSchema,
  message: z.string().optional(),
}).passthrough();

// --- CART SCHEMAS ---
export const CartItemSchema = z.object({
  id: z.string(),
  productId: z.string(),
  attributeOptionId: z.string().nullish().default(""),
  name: z.string(),
  image: z.string().nullish().default(""),
  price: z.number().default(0),
  label: z.string().nullish().default(""),
  quantity: z.number().default(1),
}).passthrough();

export const PromoSchema = z.object({
  id: z.string(),
  code: z.string(),
  discountType: z.enum(["PERCENTAGE", "FIXED"]),
  discountValue: z.number(),
  expiresAt: z.string().optional(),
});

export const AppliedPromoSchema = z.object({
  discount: z.number(),
  promo: PromoSchema,
});

export const CartSchema = z.object({
  id: z.string(),
  sessionId: z.string().nullish(),
  items: z.array(CartItemSchema).default([]),
  totalItems: z.number().default(0),
  totalPrice: z.number().default(0),
  promo: AppliedPromoSchema.nullish(),
}).passthrough();

// --- AUTH SCHEMAS ---
export const UserSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  name: z.string(),
  role: z.enum(["CLIENT", "ADMIN", "SUPERADMIN", "VENDOR"]),
  phone: z.string(),
  image: z.string().optional().nullable(),
  profileImage: z.string().optional().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).transform((data) => ({
  ...data,
  image: data.image || data.profileImage || null,
}));

export const AuthTokenSchema = z.object({
  token: z.string(),
  expires: z.string(),
});

export const AuthResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  data: z.object({
    user: UserSchema,
    tokens: z.object({
      access: AuthTokenSchema,
      refresh: AuthTokenSchema,
    }),
  }),
});

// --- ORDER SCHEMAS ---
export const OrderItemSchema = z.object({
  id: z.string(),
  orderId: z.string().nullish(),
  productId: z.string().nullish(),
  attributeOptionId: z.string().nullish(),
  nameSnapshot: z.string(),
  labelSnapshot: z.string().nullish(),
  unitPrice: z.union([z.string(), z.number()]).transform(String),
  totalPrice: z.union([z.string(), z.number()]).transform(String),
  quantity: z.number(),
  createdAt: z.string().nullish(),
  product: MinimalProductSchema.nullish(),
  attributeOption: AttributeOptionSchema.nullish(),
});

export const OrderSchema = z.object({
  id: z.string(),
  userId: z.string().nullish(),
  totalAmount: z.union([z.string(), z.number()]).transform(String),
  finalAmount: z.union([z.string(), z.number()]).default("0").transform(String),
  status: z.string(),
  paymentStatus: z.string().default("PENDING"),
  deliveryStatus: z.string().default("PENDING"),
  isTicketPrinted: z.boolean().default(false),
  promoCode: z.string().nullish(),
  createdAt: z.string().nullish(),
  updatedAt: z.string().nullish(),
  updatedById: z.string().nullish(),
  items: z.array(OrderItemSchema).default([]),
  externalRef: z.string().nullish(),
  delivery: z.any().nullish(),
});

export const OrderResponseSchema = z.object({
  data: z.array(OrderSchema),
  meta: z.object({
    total: z.number(),
    page: z.number(),
    limit: z.number(),
    totalPages: z.number(),
    hasNextPage: z.boolean().default(false),
    hasPrevPage: z.boolean().default(false),
  }),
});

export const OrderStatsSchema = z.object({
  total: z.number().default(0),
  totalSpent: z.union([z.string(), z.number()]).transform(Number).default(0),
  byStatus: z.record(z.string(), z.number()).default({}),
  byDeliveryStatus: z.record(z.string(), z.number()).default({}),
});

export const ClientOrdersResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    data: z.array(OrderSchema),
    meta: z.object({
      total: z.number(),
      page: z.number(),
      limit: z.number(),
      totalPages: z.number(),
      hasNextPage: z.boolean().default(false),
      hasPrevPage: z.boolean().default(false),
    }),
    stats: OrderStatsSchema.nullish(),
  }),
  message: z.string().nullish(),
}).passthrough();

// --- PROMO SCHEMAS ---
export const PromoCodeSchema = z.object({
  id: z.string(),
  code: z.string(),
  discountType: z.enum(["PERCENTAGE", "FIXED"]),
  discountValue: z.number(),
  minOrderAmount: z.number().optional(),
  startsAt: z.string(),
  endsAt: z.string(),
  isActive: z.boolean(),
  appliesToAll: z.boolean(),
  usageCount: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const UpdateProfileResponseSchema = z.object({
  success: z.boolean(),
  data: UserSchema,
  message: z.string().nullish(),
}).passthrough();
